import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import CustomerAccount from './customeraccount';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart, faList, faUser, faPhone, faMapMarkerAlt, faCalendarAlt,
    faFileInvoice, faBox, faRupeeSign, faTag, faBalanceScale, faDollarSign,
    faCalculator, faExclamationTriangle, faPlus, faMinus, faTrash, faSave,
    faTimes, faSpinner, faCheckCircle, faExclamationCircle, faSearch, faBarcode,
    faQrcode, faCreditCard, faMoneyBill, faUniversity, faWallet, faPercent,
    faReceipt, faChevronDown, faChevronUp, faBoxes, faHistory, faUndo, faRedo,
    faPrint, faFilePdf, faDownload, faUpload, faEdit, faChartLine, faTimesCircle,
    faShoppingBag, faAddressCard, faIdCard, faKey, faLock, faUnlock, faEye,
    faEyeSlash, faStore, faEnvelope, faGlobe, faBuilding, faPalette, faRuler,
    faScissors, faLayerGroup, faUsers, faIdBadge, faRulerCombined, faWeightHanging
} from '@fortawesome/free-solid-svg-icons';

function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

const createFabSaleTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_fab_sale_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Fab Sale Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_fab_sale_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }
        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        newFabSale: 'New Fabric Sale',
        editFabSale: 'Edit Fabric Sale',
        saleDetails: 'Fabric Sale Details',
        sellFabrics: 'Sell Fabric Products',
        billNumber: 'Bill Number',
        date: 'Date',
        shop: 'Shop',
        customerDetails: 'Customer Details',
        customerName: 'Customer Name',
        customerPhone: 'Phone Number',
        customerAddress: 'Address',
        customerGST: 'GST Number',
        searchCustomer: 'Search customer by name or phone...',
        addNewCustomer: 'Add New Customer',
        existingCustomer: 'Existing Customer',
        newCustomer: 'New Customer',
        customerCode: 'Customer Code',
        customerInfo: 'Customer Information',
        viewAccount: 'View Account',
        searchProducts: 'Search Fabric Products',
        searchPlaceholder: 'Search by name, color, batch...',
        scanBarcode: 'Scan Barcode',
        selectProduct: 'Select Product',
        noProductsFound: 'No products found',
        outOfStock: 'Out of Stock',
        lowStock: 'Low Stock',
        availableStock: 'Available Stock',
        items: 'Items',
        addItem: 'Add Item',
        productName: 'Product',
        colorCode: 'Color',
        batchNumber: 'Batch',
        rolls: 'Rolls',
        unit: 'Unit',
        price: 'Price',
        quantity: 'Qty',
        yardsPerRoll: 'Yards/Roll',
        totalYards: 'Total Yards',
        totalMeters: 'Total Meters',
        total: 'Total',
        remove: 'Remove',
        stock: 'Stock',
        calculations: 'Calculations',
        subtotal: 'Subtotal',
        discountPercent: 'Discount %',
        discountAmount: 'Discount Amount',
        taxPercent: 'Tax %',
        taxAmount: 'Tax Amount',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        balance: 'Balance',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        credit: 'Credit',
        other: 'Other',
        paymentStatus: 'Payment Status',
        paid: 'Paid',
        partial: 'Partial',
        unpaid: 'Unpaid',
        completeSale: 'Complete Sale',
        updateSale: 'Update Sale',
        clear: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        saleCompleted: 'Fabric sale completed successfully!',
        saleUpdated: 'Fabric sale updated successfully!',
        failedSave: 'Failed to save fabric sale',
        failedUpdate: 'Failed to update fabric sale',
        networkError: 'Network error occurred',
        duplicateSubmission: 'Duplicate submission detected. Please wait.',
        noShopSelected: 'No shop selected. Please select a shop first.',
        customerSelected: 'Customer selected',
        customerCreated: 'New customer created successfully',
        itemsRequired: 'At least one item is required',
        quantityExceedsStock: 'Quantity exceeds available stock',
        rollsExceedsStock: 'Rolls exceed available stock',
        invalidQuantity: 'Please enter a valid quantity',
        invalidPrice: 'Please enter a valid price',
        yard: 'Yard',
        meter: 'Meter',
        yards: 'Yards',
        meters: 'Meters',
        roll: 'Roll',
        bundle: 'Bundle',
        selectUnit: 'Select unit',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        todaySales: "Today's Sales",
        totalSales: 'Total Sales',
        totalAmount: 'Total Amount',
        totalPaid: 'Total Paid',
        totalBalance: 'Total Balance',
        notes: 'Notes',
        printReceipt: 'Print Receipt',
        receipt: 'FABRIC RECEIPT',
        paymentDetails: 'Payment Details',
        thankYou: 'Thank you for your business!',
        footer: '© auto year ourzest pvt ltd',
        printerSettings: 'Printer Settings',
        selectPrinter: 'Select Printer',
        paperSize: 'Paper Size',
        thermalPrinter: 'Thermal Printer',
        standardPrinter: 'Standard Printer',
        copies: 'Copies',
        businessInfo: 'Business Information',
        invoice: 'FABRIC INVOICE',
        gst: 'GST',
        cashier: 'Cashier',
        transactionId: 'Transaction ID',
        poweredBy: 'Powered by OurZest',
        itemsSold: 'Items Sold',
        printing: 'Printing receipt...',
        printSuccess: 'Receipt sent to printer',
        close: 'Close',
        print: 'Print',
        fabricType: 'Fabric Type',
        color: 'Color',
        orderType: 'Order Type',
        shopOrder: 'Shop',
        fieldOrder: 'Field Order',
        currency: 'Currency',
        currencyRate: 'Exchange Rate',
        okay: 'OK',
        noCustomersFound: 'No customers found',
        time: 'Time',
        availableYards: 'Available Yards',
        availableMeters: 'Available Meters',
        sellInYards: 'Sell in Yards',
        sellInMeters: 'Sell in Meters',
        stockInYards: 'Stock in Yards',
        stockInMeters: 'Stock in Meters'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }
        const translations = {};
        const promises = [];
        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });
        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }
        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();
    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const fabSaleTranslator = createFabSaleTranslator();

const CurrencySelector = ({ currentLanguage, onCurrencyChange, onRateChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [exchangeRates, setExchangeRates] = useState({});
    const [selectedCurrency, setSelectedCurrency] = useState(() => {
        const saved = localStorage.getItem('ourzest_currency');
        return saved ? JSON.parse(saved) : { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', flag: '🇵🇰', shortcut: 'P' };
    });
    const [currentRate, setCurrentRate] = useState(1.0000);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    const currencies = [
        { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', flag: '🇵🇰', shortcut: 'P', fullName: 'Pakistani Rupee' },
        { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', flag: '🇦🇪', shortcut: 'D', fullName: 'UAE Dirham' },
        { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸', shortcut: 'U', fullName: 'US Dollar' },
        { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳', shortcut: 'Y', fullName: 'Chinese Yuan' },
        { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺', shortcut: 'E', fullName: 'Euro' },
        { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧', shortcut: 'G', fullName: 'British Pound' },
        { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳', shortcut: 'I', fullName: 'Indian Rupee' },
        { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', flag: '🇸🇦', shortcut: 'S', fullName: 'Saudi Riyal' },
        { code: 'QAR', symbol: '﷼', name: 'Qatari Riyal', flag: '🇶🇦', shortcut: 'Q', fullName: 'Qatari Riyal' },
        { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar', flag: '🇰🇼', shortcut: 'K', fullName: 'Kuwaiti Dinar' }
    ];

    useEffect(() => {
        const fetchExchangeRates = async () => {
            try {
                const response = await fetch('https://api.exchangerate-api.com/v4/latest/PKR');
                const data = await response.json();
                if (data.rates) {
                    setExchangeRates(data.rates);
                    if (selectedCurrency.code !== 'PKR') {
                        const rate = data.rates[selectedCurrency.code] || 1.0000;
                        setCurrentRate(rate);
                        if (onRateChange) onRateChange(rate);
                    }
                }
            } catch (error) {
                console.error('Error fetching exchange rates:', error);
                const fallbackRates = {
                    'USD': 0.0036, 'AED': 0.013, 'CNY': 0.026, 'EUR': 0.0033,
                    'GBP': 0.0028, 'INR': 0.30, 'SAR': 0.013, 'QAR': 0.013, 'KWD': 0.0011
                };
                setExchangeRates(fallbackRates);
                if (selectedCurrency.code !== 'PKR') {
                    const rate = fallbackRates[selectedCurrency.code] || 1.0000;
                    setCurrentRate(rate);
                    if (onRateChange) onRateChange(rate);
                }
            }
        };
        fetchExchangeRates();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'SELECT') return;
            const key = event.key.toUpperCase();
            const currency = currencies.find(c => c.shortcut === key);
            if (currency) { event.preventDefault(); handleCurrencySelect(currency); }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [exchangeRates]);

    const filteredCurrencies = currencies.filter(currency =>
        currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        if (!isOpen) setTimeout(() => { searchInputRef.current?.focus(); }, 100);
    };

    const handleCurrencySelect = (currency) => {
        const rate = currency.code === 'PKR' ? 1.0000 : (exchangeRates[currency.code] || 1.0000);
        setSelectedCurrency(currency);
        setCurrentRate(rate);
        localStorage.setItem('ourzest_currency', JSON.stringify(currency));
        setIsOpen(false);
        setSearchTerm('');
        if (onCurrencyChange) onCurrencyChange(currency);
        if (onRateChange) onRateChange(rate);
    };

    const handleRateChange = (e) => {
        const newRate = parseFloat(e.target.value) || 1.0000;
        setCurrentRate(newRate);
        if (onRateChange) onRateChange(newRate);
    };

    return (
        <div className="currency-selector" ref={dropdownRef}>
            <div className="currency-selector-row">
                <button className={`currency-button ${isOpen ? 'active' : ''}`} onClick={toggleDropdown}
                    title={`${selectedCurrency.code} - Press ${selectedCurrency.shortcut} for shortcut`}>
                    <span className="currency-flag">{selectedCurrency.flag}</span>
                    <span className="currency-code">{selectedCurrency.code}</span>
                    <span className="currency-symbol">{selectedCurrency.symbol}</span>
                    <FontAwesomeIcon icon={faChevronDown} />
                    {!isOpen && <span className="currency-shortcut-hint">(<span className="key-hint">{selectedCurrency.shortcut}</span>)</span>}
                </button>
                <div className="currency-rate-field">
                    <input
                        type="number"
                        id="currency_rate"
                        name="currency_rate"
                        value={currentRate}
                        onChange={handleRateChange}
                        step={selectedCurrency.code === 'PKR' ? "0.01" : "0.0001"}
                        min="0.0001"
                        className="rate-input-field"
                        disabled={selectedCurrency.code === 'PKR'}
                        title={selectedCurrency.code === 'PKR' ? 'Rate fixed at 1.0000 for PKR' : `Exchange rate for ${selectedCurrency.code}`}
                    />
                </div>
            </div>
            {isOpen && (
                <div className="currency-dropdown">
                    <div className="currency-search">
                        <FontAwesomeIcon icon={faSearch} className="currency-search-icon" />
                        <input ref={searchInputRef} type="text" placeholder="Search currency..." value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)} dir={isRTL ? 'rtl' : 'ltr'} />
                    </div>
                    <div className="currency-shortcuts">
                        <span className="shortcut-key"><span className="key">P</span> PKR</span>
                        <span className="shortcut-key"><span className="key">D</span> DIR</span>
                        <span className="shortcut-key"><span className="key">U</span> USD</span>
                        <span className="shortcut-key"><span className="key">Y</span> YAN</span>
                    </div>
                    <div className="currency-list">
                        {filteredCurrencies.length > 0 ? (
                            filteredCurrencies.map((currency) => (
                                <div key={currency.code} className={`currency-item ${selectedCurrency.code === currency.code ? 'selected' : ''}`}
                                    onClick={() => handleCurrencySelect(currency)}>
                                    <div className="currency-info">
                                        <span className="currency-flag">{currency.flag}</span>
                                        <div className="currency-details">
                                            <span className="currency-name">{currency.code}</span>
                                            <span className="currency-fullname">{currency.fullName}</span>
                                        </div>
                                    </div>
                                    <div className="currency-symbol">{currency.symbol}</div>
                                    {currency.shortcut && <span className="shortcut-hint">{currency.shortcut}</span>}
                                </div>
                            ))
                        ) : (
                            <div className="currency-no-results"><FontAwesomeIcon icon={faExclamationTriangle} /><span>No currencies found</span></div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationCircle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };
    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };
    if (!toast.show) return null;
    const opacity = Math.max(toast.progress / 100, 0.1);
    return (
        <div className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`} style={{ opacity }}>
            <div className="toast-content">
                <div className="toast-icon"><FontAwesomeIcon icon={getIconForType(toast.type)} /></div>
                <div className="toast-text">
                    <div className="toast-title">{getTitleForType(toast.type)}</div>
                    <div className="toast-message">{toast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div className="toast-progress-bar" style={{ width: `${toast.progress}%`, transition: 'width 0.1s linear' }}></div>
                <div className="toast-countdown">{toast.timeLeft}s</div>
            </div>
        </div>
    );
};

const CustomerInfoModal = ({ show, customer, onClose, translations, isRTL }) => {
    if (!show || !customer) return null;
    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern customer-info-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header-modern">
                    <h3><FontAwesomeIcon icon={faIdCard} />{translations?.customerInfo || 'Customer Information'}</h3>
                    <button className="modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <div className="modal-body-modern">
                    <div className="customer-info-badge">
                        <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
                        <span className="customer-name">{customer.name}</span>
                        {customer.is_new && (<span className="customer-status">{translations?.newCustomer || 'New Customer'}</span>)}
                    </div>
                    <div className="customer-details-grid">
                        {customer.customer_code && (
                            <div className="detail-item">
                                <div className="detail-label"><FontAwesomeIcon icon={faIdCard} />{translations?.customerCode || 'Customer Code'}:</div>
                                <div className="detail-value"><code>{customer.customer_code}</code></div>
                            </div>
                        )}
                        {customer.phone && (
                            <div className="detail-item">
                                <div className="detail-label"><FontAwesomeIcon icon={faPhone} />{translations?.customerPhone || 'Phone'}:</div>
                                <div className="detail-value">{customer.phone}</div>
                            </div>
                        )}
                        {customer.gst_no && (
                            <div className="detail-item">
                                <div className="detail-label"><FontAwesomeIcon icon={faTag} />GST:</div>
                                <div className="detail-value">{customer.gst_no}</div>
                            </div>
                        )}
                        {customer.address && (
                            <div className="detail-item full-width">
                                <div className="detail-label"><FontAwesomeIcon icon={faMapMarkerAlt} />{translations?.customerAddress || 'Address'}:</div>
                                <div className="detail-value">{customer.address}</div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="modal-footer-modern">
                    <button className="btn-modern btn-primary-modern" onClick={onClose}>{translations?.okay || 'OK'}</button>
                </div>
            </div>
        </div>
    );
};

const FabReceiptModal = ({ show, sale, businessInfo, shopInfo, translations, onPrint, onClose, currencySymbol }) => {
    const receiptRef = useRef(null);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
        }
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatTime = () => {
        return new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    };

    const getPaymentStatus = (paid, grandTotal) => {
        const paidAmount = parseFloat(paid) || 0;
        const totalAmount = parseFloat(grandTotal) || 0;
        const balance = totalAmount - paidAmount;
        
        if (balance <= 0) {
            return { status: 'paid', text: translations?.paid || 'Paid', color: '#10b981' };
        } else if (paidAmount === 0) {
            return { status: 'unpaid', text: translations?.unpaid || 'Unpaid', color: '#ef4444' };
        } else {
            return { status: 'partial', text: translations?.partial || 'Partially Paid', color: '#f59e0b' };
        }
    };

   const handlePrintInvoice = () => {
    if (!sale) return;

    const shopName = businessInfo?.businessname || shopInfo?.name || 'Fabric Shop';
    const shopAddress = businessInfo?.businessaddress || '';
    const shopPhone = businessInfo?.phonenumber || '';
    const currencySym = currencySymbol || sale?.currency_symbol || 'Rs';

    // Format date function - FIXED
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return new Date().toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format time function
    const formatTime = () => {
        return new Date().toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    // Process items
    let calculatedSubtotal = 0;
    const itemsList = sale.items || [];

    const processedItems = itemsList.map(item => {
        const quantity = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price_per_unit || item.price || 0);
        const total = parseFloat(item.total) || (quantity * price);
        calculatedSubtotal += total;

        // Create product display name with color code
        let productDisplayName = item.product_name || 'Product';
        if (item.product_colorcode) {
            productDisplayName = `${productDisplayName} - ${item.product_colorcode}`;
        }

        return {
            ...item,
            product_name: productDisplayName,
            product_original: item.product_name || 'Product',
            color_code: item.product_colorcode || '',
            quantity,
            price,
            total,
            unit: item.unit === 'meter' ? 'Mtr' : (item.unit === 'yards' ? 'Yd' : (item.unit || '')),
            rolls: item.rolls || 0,
            batch_no: item.batch_no || ''
        };
    });

    // Totals
    const subtotal = sale.subtotal || calculatedSubtotal.toFixed(2);
    const tax = sale.tax_amount || '0.00';
    const discount = sale.discount_amount || '0.00';
    const grandTotal = sale.grand_total || (
        parseFloat(subtotal) + parseFloat(tax) - parseFloat(discount)
    ).toFixed(2);
    const paidAmount = sale.paid_amount || grandTotal;
    const balance = sale.balance || (
        parseFloat(grandTotal) - parseFloat(paidAmount)
    ).toFixed(2);

    const getPaymentStatus = (paid, grandTotal) => {
        const paidAmount = parseFloat(paid) || 0;
        const totalAmount = parseFloat(grandTotal) || 0;
        const balanceAmount = totalAmount - paidAmount;
        
        if (balanceAmount <= 0) {
            return { status: 'paid', text: 'PAID', color: '#10b981' };
        } else if (paidAmount === 0) {
            return { status: 'unpaid', text: 'UNPAID', color: '#ef4444' };
        } else {
            return { status: 'partial', text: 'PARTIAL', color: '#f59e0b' };
        }
    };

    const paymentStatus = getPaymentStatus(paidAmount, grandTotal);

    const printWindow = window.open('', '_blank');

    if (printWindow) {
        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Invoice - ${sale.bill_number || 'Invoice'}</title>
            <meta charset="UTF-8" />
            <style>
                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }
                
                @page {
                    size: A5;
                    margin: 0;
                }
                
                body {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    background: #ffffff;
                    color: #111827;
                    line-height: 1.3;
                }
                
                .invoice {
                    width: 100%;
                    max-width: 148mm;
                    margin: 0 auto;
                    padding: 3.5mm 4mm;
                    background: white;
                }
                
                .invoice-header {
                    text-align: center;
                    padding-bottom: 2.5mm;
                    border-bottom: 1px solid #dbe2ea;
                }
                
                .shop-name {
                    font-size: 18pt;
                    font-weight: 800;
                    color: #111827;
                    margin-bottom: 0.8mm;
                    letter-spacing: -0.3px;
                }
                
                .shop-address {
                    font-size: 7pt;
                    color: #6b7280;
                    margin-bottom: 0.5mm;
                }
                
                .shop-contact {
                    font-size: 7pt;
                    color: #374151;
                    font-weight: 600;
                }
                
                .invoice-title {
                    margin-top: 2mm;
                    text-align: center;
                    font-size: 10pt;
                    font-weight: 800;
                    letter-spacing: 1px;
                    color: #111827;
                }
                
                .info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1.2mm 4mm;
                    padding: 2mm 0;
                    margin-top: 2mm;
                    border-bottom: 1px dashed #d1d5db;
                }
                
                .info-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .info-label {
                    font-size: 7pt;
                    font-weight: 700;
                    color: #6b7280;
                }
                
                .info-value {
                    font-size: 7.2pt;
                    font-weight: 600;
                    color: #111827;
                    text-align: right;
                }
                
                .customer-section {
                    margin-top: 2mm;
                    padding-bottom: 2mm;
                    border-bottom: 1px dashed #d1d5db;
                }
                
                .items-table {
                    width: 100%;
                    margin-top: 3mm;
                    border-collapse: collapse;
                    font-size: 7.2pt;
                }
                
                .items-table thead th {
                    background: #f3f4f6;
                    color: #111827;
                    font-weight: 800;
                    padding: 2mm 1.5mm;
                    border: 1px solid #d1d5db;
                    text-transform: uppercase;
                    font-size: 6.8pt;
                }
                
                .items-table tbody td {
                    padding: 2mm 1.5mm;
                    border: 1px solid #e5e7eb;
                    vertical-align: middle;
                }
                
                .items-table tbody tr:nth-child(even) {
                    background: #fafafa;
                }
                
                .product-name {
                    font-size: 7.2pt;
                    font-weight: 700;
                    color: #111827;
                    line-height: 1.3;
                }
                
                .product-sub {
                    margin-top: 0.5mm;
                    font-size: 6pt;
                    color: #6b7280;
                }
                
                .text-right {
                    text-align: right;
                }
                
                .text-center {
                    text-align: center;
                }
                
                .totals-section {
                    width: 48%;
                    margin-left: auto;
                    margin-top: 3mm;
                }
                
                .total-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.2mm 0;
                    font-size: 7.5pt;
                }
                
                .total-row .label {
                    font-weight: 700;
                    color: #4b5563;
                }
                
                .total-row .value {
                    font-weight: 700;
                    color: #111827;
                }
                
                .grand-total-row {
                    margin-top: 1mm;
                    padding-top: 1.5mm;
                    border-top: 1.5px solid #111827;
                }
                
                .grand-total-row .label,
                .grand-total-row .value {
                    font-size: 9pt;
                    font-weight: 800;
                    color: #111827;
                }
                
                .payment-info {
                    margin-top: 3mm;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-top: 2mm;
                    border-top: 1px dashed #d1d5db;
                    font-size: 7pt;
                    font-weight: 600;
                }
                
                .notes-box {
                    margin-top: 2mm;
                    padding-top: 1.5mm;
                    border-top: 1px dashed #d1d5db;
                    font-size: 6.8pt;
                    color: #374151;
                }
                
                .footer {
                    margin-top: 4mm;
                    padding-top: 2mm;
                    border-top: 1px dashed #d1d5db;
                    text-align: center;
                }
                
                .thankyou {
                    font-size: 8pt;
                    font-weight: 800;
                    color: #111827;
                }
                
                .footer-text {
                    font-size: 6.5pt;
                    color: #6b7280;
                    margin-top: 0.5mm;
                }
                
                @media print {
                    body {
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .invoice {
                        padding: 3mm;
                    }
                    table tr {
                        break-inside: avoid;
                    }
                }
            </style>
        </head>
        <body>
            <div class="invoice">
                <!-- HEADER -->
                <div class="invoice-header">
                    <div class="shop-name">${shopName.replace(/[<>]/g, '')}</div>
                    ${shopAddress ? `<div class="shop-address">${shopAddress.replace(/[<>]/g, '')}</div>` : ''}
                    ${shopPhone ? `<div class="shop-contact">📞 ${shopPhone.replace(/[<>]/g, '')}</div>` : ''}
                    <div class="invoice-title">${translations?.invoice || 'TAX INVOICE'}</div>
                </div>
                
                <!-- INFO -->
                <div class="info-grid">
                    <div class="info-item">
                        <span class="info-label">${translations?.billNumber || 'Bill No'}</span>
                        <span class="info-value">#${sale.bill_number}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">${translations?.date || 'Date'}</span>
                        <span class="info-value">${formatDate(sale.date || sale.date)}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">${translations?.time || 'Time'}</span>
                        <span class="info-value">${formatTime()}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">${translations?.cashier || 'Cashier'}</span>
                        <span class="info-value">${(sale.username || 'Staff').replace(/[<>]/g, '')}</span>
                    </div>
                </div>
                
                <!-- CUSTOMER -->
                ${(sale.customer_name || sale.customer_phone || sale.customer_address) ? `
                    <div class="customer-section">
                        <div class="info-grid" style="margin-top:0; padding:0; border:none;">
                            ${sale.customer_name ? `
                                <div class="info-item">
                                    <span class="info-label">${translations?.customerName || 'Customer'}</span>
                                    <span class="info-value">${sale.customer_name.replace(/[<>]/g, '')}</span>
                                </div>
                            ` : ''}
                            ${sale.customer_phone ? `
                                <div class="info-item">
                                    <span class="info-label">${translations?.phone || 'Phone'}</span>
                                    <span class="info-value">${sale.customer_phone.replace(/[<>]/g, '')}</span>
                                </div>
                            ` : ''}
                            ${sale.customer_address ? `
                                <div class="info-item" style="grid-column:1/-1;">
                                    <span class="info-label">${translations?.customerAddress || 'Address'}</span>
                                    <span class="info-value">${sale.customer_address.replace(/[<>]/g, '')}</span>
                                </div>
                            ` : ''}
                            ${sale.customer_gst ? `
                                <div class="info-item" style="grid-column:1/-1;">
                                    <span class="info-label">${translations?.customerGST || 'GST'}</span>
                                    <span class="info-value">${sale.customer_gst.replace(/[<>]/g, '')}</span>
                                </div>
                            ` : ''}
                        </div>
                    </div>
                ` : ''}
                
                <!-- PRODUCTS -->
                <table class="items-table">
                    <thead>
                        <tr>
                            <th style="width:38%">${translations?.productName || 'Product'}</th>
                            <th style="width:8%" class="text-center">${translations?.batchNo || 'Batch'}</th>
                            <th style="width:8%" class="text-center">${translations?.unit || 'Unit'}</th>
                            <th style="width:8%" class="text-center">${translations?.rolls || 'Rolls'}</th>
                            <th style="width:9%" class="text-center">${translations?.quantity || 'Qty'}</th>
                            <th style="width:14%" class="text-right">${translations?.price || 'Price'}</th>
                            <th style="width:15%" class="text-right">${translations?.total || 'Total'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${processedItems.length > 0
                ? processedItems.map(item => `
                                <tr>
                                    <td>
                                        <div class="product-name">${item.product_name.replace(/[<>]/g, '')}</div>
                                    </td>
                                    <td class="text-center">${(item.batch_no || '-').replace(/[<>]/g, '')}</td>
                                    <td class="text-center">${item.unit || '-'}</td>
                                    <td class="text-center">${item.rolls || 0}</td>
                                    <td class="text-center"><strong>${item.quantity.toFixed(2)}</strong></td>
                                    <td class="text-right">${currencySym} ${item.price.toFixed(2)}</td>
                                    <td class="text-right"><strong>${currencySym} ${item.total.toFixed(2)}</strong></td>
                                `
                ).join('')
                : `
                                <tr>
                                    <td colspan="7" class="text-center" style="padding:5mm; color:#ef4444;">
                                        No products found
                                    </td>
                                `
            }
                    </tbody>
                </table>
                
                <!-- TOTALS -->
                <div class="totals-section">
                    <div class="total-row">
                        <span class="label">${translations?.subtotal || 'Subtotal'}</span>
                        <span class="value">${currencySym} ${parseFloat(subtotal).toFixed(2)}</span>
                    </div>
                    ${parseFloat(tax) > 0 ? `
                        <div class="total-row">
                            <span class="label">${translations?.taxAmount || 'Tax'}</span>
                            <span class="value">${currencySym} ${parseFloat(tax).toFixed(2)}</span>
                        </div>
                    ` : ''}
                    ${parseFloat(discount) > 0 ? `
                        <div class="total-row">
                            <span class="label">${translations?.discountAmount || 'Discount'}</span>
                            <span class="value" style="color:#059669;">-${currencySym} ${parseFloat(discount).toFixed(2)}</span>
                        </div>
                    ` : ''}
                    <div class="total-row grand-total-row">
                        <span class="label">${translations?.grandTotal || 'Grand Total'}</span>
                        <span class="value">${currencySym} ${parseFloat(grandTotal).toFixed(2)}</span>
                    </div>
                    <div class="total-row">
                        <span class="label">${translations?.paidAmount || 'Paid'}</span>
                        <span class="value">${currencySym} ${parseFloat(paidAmount).toFixed(2)}</span>
                    </div>
                    <div class="total-row">
                        <span class="label">${translations?.balance || 'Balance'}</span>
                        <span class="value">${currencySym} ${parseFloat(balance).toFixed(2)}</span>
                    </div>
                </div>
                
                <!-- PAYMENT -->
                <div class="payment-info">
                    <span>
                        ${translations?.paymentMethod || 'Payment'}: 
                        <strong>${(sale.payment_method || 'CASH').toUpperCase()}</strong>
                    </span>
                    <span>
                        ${translations?.orderType || 'Order Type'}: 
                        <strong>${(sale.o_status === 'shop' ? 'SHOP' : 'FIELD').toUpperCase()}</strong>
                    </span>
                    <span>
                        ${translations?.paymentStatus || 'Status'}: 
                        <strong>${paymentStatus.text.toUpperCase()}</strong>
                    </span>
                </div>
                
                <!-- NOTES -->
                ${sale.notes ? `
                    <div class="notes-box">
                        📝 ${translations?.notes || 'Note'}: ${sale.notes.replace(/[<>]/g, '')}
                    </div>
                ` : ''}
                
                <!-- FOOTER -->
                <div class="footer">
                    <div class="thankyou">${translations?.thankYou || 'Thank you for your business!'}</div>
                </div>
            </div>
        </body>
        </html>
    `);

        printWindow.document.close();
        printWindow.focus();

        setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = () => {
                printWindow.close();
            };
        }, 300);
    }
    
    if (onPrint) onPrint();
};

    if (!show || !sale) return null;

    const shopName = shopInfo?.name || businessInfo?.businessname || 'Fabric Shop';
    const shopAddress = businessInfo?.businessaddress || '';
    const shopPhone = businessInfo?.phonenumber || '';
    const currencySym = currencySymbol || sale?.currency_symbol || 'Rs';

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern receipt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
                <div className="modal-header-modern" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3><FontAwesomeIcon icon={faReceipt} style={{ marginRight: '8px' }} />{translations?.invoice || 'FABRIC INVOICE'} (A5)</h3>
                    <button className="modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <div className="modal-body-modern" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0' }}>
                    <div ref={receiptRef} className="a5-invoice-container" style={{ padding: '4mm' }}>
                        <div className="invoice-header">
                            <div className="shop-name">{shopName}</div>
                            {shopAddress && <div className="shop-address">{shopAddress}</div>}
                            {shopPhone && <div className="shop-contact">Tel: {shopPhone}</div>}
                        </div>
                        <div className="invoice-title">{translations?.invoice || 'TAX INVOICE'}</div>
                        <div className="info-row">
                            <span><span className="info-label">{translations?.billNumber || 'Bill No'}:</span> <span className="info-value">#{sale?.bill_number || 'N/A'}</span></span>
                            <span><span className="info-label">{translations?.date || 'Date'}:</span> <span className="info-value">{formatDate(sale?.sale_date)}</span></span>
                            <span><span className="info-label">{translations?.time || 'Time'}:</span> <span className="info-value">{formatTime()}</span></span>
                        </div>
                        <div className="info-row">
                            <span><span className="info-label">{translations?.cashier || 'Cashier'}:</span> <span className="info-value">{sale?.username || 'Staff'}</span></span>
                            {sale?.o_status && (
                                <span><span className="info-label">{translations?.orderType || 'Order Type'}:</span> <span className="info-value">{sale.o_status === 'shop' ? (translations?.shopOrder || 'Shop') : (translations?.fieldOrder || 'Field Order')}</span></span>
                            )}
                        </div>
                        {(sale?.customer_name || sale?.customer_phone || sale?.customer_gst || sale?.customer_address) && (
                            <div className="customer-section">
                                <strong>{translations?.customerDetails || 'Customer Details'}</strong><br />
                                {sale.customer_name && <div><strong>{sale.customer_name}</strong></div>}
                                {sale.customer_phone && <div>📞 {sale.customer_phone}</div>}
                                {sale.customer_gst && <div>GST: {sale.customer_gst}</div>}
                                {sale.customer_address && <div>📍 {sale.customer_address}</div>}
                            </div>
                        )}
                        <table className="items-table">
                            <thead>
                                <tr><th>#</th><th>{translations?.productName || 'Product Details'}</th><th className="text-center">{translations?.quantity || 'Qty'}</th><th className="text-center">{translations?.rolls || 'Rolls'}</th><th className="text-center">{translations?.unit || 'Unit'}</th><th className="text-right">{translations?.price || 'Price'}</th><th className="text-right">{translations?.total || 'Total'}</th></tr>
                            </thead>
                            <tbody>
                                {sale?.items?.map((item, idx) => (
                                    <tr key={idx}>
                                        <td className="text-center">{idx + 1}</td>
                                        <td>
                                            <strong>{item.product_name}</strong>
                                            <div className="product-details">
                                                {item.product_colorcode && <div> Color: {item.product_colorcode}</div>}
                                                {item.batch_no && <div>🏷️ Batch: {item.batch_no}</div>}
                                            </div>
                                        </td>
                                        <td className="text-center">{parseFloat(item.quantity).toFixed(2)}</td>
                                        <td className="text-center">{parseInt(item.rolls || 0)}</td>
                                        <td className="text-center">{item.unit === 'meter' ? 'Mtr' : 'Yd'}</td>
                                        <td className="text-right">{currencySym} {parseFloat(item.price_per_unit).toFixed(2)}</td>
                                        <td className="text-right">{currencySym} {parseFloat(item.total || item.price_per_unit * item.quantity).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="totals-section">
                            <div className="total-row"><span className="label">{translations?.subtotal || 'Subtotal'}:</span><span className="value">{currencySym} {parseFloat(sale?.subtotal || 0).toFixed(2)}</span></div>
                            {parseFloat(sale?.discount_amount) > 0 && (<div className="total-row"><span className="label">{translations?.discountAmount || 'Discount'}:</span><span className="value">-{currencySym} {parseFloat(sale?.discount_amount).toFixed(2)}</span></div>)}
                            {parseFloat(sale?.tax_amount) > 0 && (<div className="total-row"><span className="label">{translations?.taxAmount || 'Tax'}:</span><span className="value">+{currencySym} {parseFloat(sale?.tax_amount).toFixed(2)}</span></div>)}
                            <div className="total-row grand-total-row"><span className="label">{translations?.grandTotal || 'GRAND TOTAL'}:</span><span className="value">{currencySym} {parseFloat(sale?.grand_total).toFixed(2)}</span></div>
                        </div>
                        <div className="payment-info">
                            <span><span className="info-label">{translations?.paymentMethod || 'Payment'}:</span> {sale?.payment_method?.toUpperCase() || 'CASH'}</span>
                            <span><span className="info-label">{translations?.paidAmount || 'Paid'}:</span> {currencySym} {parseFloat(sale?.paid_amount).toFixed(2)}</span>
                            <span><span className="info-label">{translations?.balance || 'Balance'}:</span> {currencySym} {parseFloat(sale?.balance).toFixed(2)}</span>
                        </div>
                        {sale?.notes && (<div style={{ margin: '3mm 0', padding: '2mm', background: '#fef3c7', borderRadius: '4px', fontSize: '8pt' }}>📝 {translations?.notes || 'Notes'}: {sale.notes}</div>)}
                        <div className="footer"><div className="thankyou">{translations?.thankYou || 'Thank you for your business!'}</div><div>{translations?.poweredBy || 'Powered by OurZest'} | {new Date().getFullYear()}</div></div>
                    </div>
                </div>
                <div className="modal-footer-modern" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '15px', borderTop: '1px solid #eee' }}>
                    <button className="btn-modern btn-secondary-modern" onClick={onClose}><FontAwesomeIcon icon={faTimes} />{translations?.close || 'Close'}</button>
                    <button className="btn-modern btn-primary-modern" onClick={handlePrintInvoice}><FontAwesomeIcon icon={faPrint} /> {translations?.print || 'Print A5 Invoice'}</button>
                </div>
            </div>
        </div>
    );
};

const BusinessInfo = ({ businessInfo, translations }) => {
    if (!businessInfo) return null;
    return (
        <div className="business-info-card" style={{ background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', borderRadius: '12px', display: 'flex', alignItems: 'center' }}>
            {businessInfo.bussinesslogo && (<img src={businessInfo.bussinesslogo} alt="Logo" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />)}
            <div className="business-details">
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{businessInfo.businessname}</div>
                {businessInfo.businessaddress && (<div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}><FontAwesomeIcon icon={faMapMarkerAlt} size="xs" style={{ marginRight: '5px' }} />{businessInfo.businessaddress}</div>)}
                {businessInfo.phonenumber && (<div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}><FontAwesomeIcon icon={faPhone} size="xs" style={{ marginRight: '5px' }} />{businessInfo.phonenumber}</div>)}
            </div>
        </div>
    );
};

const CustomerAccountModal = ({ show, customerId, onClose, translations }) => {
    if (!show) return null;
    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern large" onClick={e => e.stopPropagation()} style={{ maxWidth: '1200px', width: '90%' }}>
                <div className="modal-header-modern">
                    <h3><FontAwesomeIcon icon={faUsers} />{translations?.customerAccount || 'Customer Account'}</h3>
                    <button className="modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <div className="modal-body-modern" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    <CustomerAccount customerId={customerId} embedded={true} />
                </div>
            </div>
        </div>
    );
};

// Items Table with proper unit handling (yards/meters) AND rolls
const FabItemsTable = ({ items, onItemChange, onRemove, onAdd, errors, disabled, translations, isRTL, currencySymbol }) => {
    const tableRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => { setIsMobile(window.innerWidth <= 768); };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const calculateTotal = (price, quantity) => {
        const p = parseFloat(price) || 0;
        const q = parseFloat(quantity) || 0;
        return (p * q).toFixed(2);
    };

    const units = [
        { value: 'yards', label: translations?.yards || 'Yards' },
        { value: 'meters', label: translations?.meters || 'Meters' },
    ];

    // Calculate available stock based on selected unit using total_yards and total_meters
    const getAvailableStock = (item, unit) => {
        if (unit === 'yards') {
            return parseFloat(item.total_yards || 0);
        } else {
            return parseFloat(item.total_meters || 0);
        }
    };

    if (isMobile) {
        return (
            <div className="items-mobile-container">
                <div className="items-header-mobile">
                    <h4><FontAwesomeIcon icon={faBoxes} className="header-icon-only" />{translations?.items || 'Items'}</h4>
                    <button type="button" className="btn-add-item-mobile" onClick={onAdd} disabled={disabled}>
                        <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                    </button>
                </div>
                <div className="items-cards-container">
                    {items.map((item, index) => {
                        const availableStock = getAvailableStock(item, item.unit);
                        const isLowStock = availableStock <= (item.stock_alert || 5);
                        const maxRolls = parseInt(item.total_rolls || 0);
                        const currentRolls = parseInt(item.rolls || 0);
                        const isRollsExceed = currentRolls > maxRolls;
                        return (
                            <div key={index} className="item-card-mobile">
                                <div className="item-card-header">
                                    <span className="item-number"><FontAwesomeIcon icon={faBox} className="header-icon-only" />Item {index + 1}</span>
                                    <button type="button" className="item-remove-btn-mobile" onClick={() => onRemove(index)} disabled={disabled || items.length === 1}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                                <div className="item-card-content">
                                    <div className="mobile-field">
                                        <label><FontAwesomeIcon icon={faBox} />{translations?.productName || 'Product'}</label>
                                        <input type="text" value={item.product_name} readOnly className="readonly-input" />
                                    </div>
                                    <div className="mobile-row">
                                        <div className="mobile-field half"><label>{translations?.colorCode || 'Color'}</label><input type="text" value={item.product_colorcode || ''} readOnly /></div>
                                        <div className="mobile-field half"><label>{translations?.batchNumber || 'Batch'}</label><input type="text" value={item.batch_no || ''} readOnly /></div>
                                    </div>
                                    <div className="mobile-field stock-info">
                                        <label>{translations?.availableStock || 'Available Stock'}</label>
                                        <div className={`stock-badge ${isLowStock ? 'low-stock' : ''}`}>
                                            {availableStock.toFixed(2)} {item.unit === 'yards' ? (translations?.yards || 'Yards') : (translations?.meters || 'Meters')}
                                            {item.total_rolls > 0 && <span className="rolls-info"> ({item.total_rolls} {translations?.roll || 'Rolls'})</span>}
                                        </div>
                                    </div>
                                    <div className="mobile-row">
                                        <div className="mobile-field half">
                                            <label>{translations?.rolls || 'Rolls'}</label>
                                            <input 
                                                type="number" 
                                                value={item.rolls || 0} 
                                                onChange={(e) => onItemChange(index, 'rolls', e.target.value)} 
                                                step="1" 
                                                min="0" 
                                                max={maxRolls}
                                                placeholder="Rolls"
                                                disabled={disabled}
                                                className={isRollsExceed ? 'error' : ''}
                                            />
                                            {isRollsExceed && <div className="error-text-small">Max: {maxRolls}</div>}
                                        </div>
                                        <div className="mobile-field half">
                                            <label>{translations?.unit || 'Unit'}</label>
                                            <select value={item.unit} onChange={(e) => onItemChange(index, 'unit', e.target.value)} disabled={disabled}>
                                                {units.map((unit) => (<option key={unit.value} value={unit.value}>{unit.label}</option>))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="mobile-row">
                                        <div className={`mobile-field half ${errors[`price_${index}`] ? 'has-error' : ''}`}>
                                            <label>{translations?.price || 'Price'} *</label>
                                            <input type="number" value={item.price_per_unit} onChange={(e) => onItemChange(index, 'price_per_unit', e.target.value)} step="0.01" min="0" placeholder="0.00" disabled={disabled} />
                                        </div>
                                        <div className={`mobile-field half ${errors[`quantity_${index}`] ? 'has-error' : ''}`}>
                                            <label>{translations?.quantity || 'Qty'} *</label>
                                            <input type="number" value={item.quantity} onChange={(e) => onItemChange(index, 'quantity', e.target.value)} step="0.001" min="0.001" max={availableStock} placeholder="0.000" disabled={disabled} />
                                        </div>
                                    </div>
                                    <div className="mobile-field">
                                        <label>{translations?.total || 'Total'}</label>
                                        <input type="text" value={`${currencySymbol} ${calculateTotal(item.price_per_unit, item.quantity)}`} readOnly disabled className="readonly-input total-display" />
                                    </div>
                                    {errors[`quantity_${index}`] && (<div className="error-text"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`quantity_${index}`]}</div>)}
                                    {errors[`rolls_${index}`] && (<div className="error-text"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`rolls_${index}`]}</div>)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="items-table-container">
            <div className="table-header-actions">
                <h4><FontAwesomeIcon icon={faBoxes} className="header-icon-only" />{translations?.items || 'Items'}</h4>
                <button type="button" className="btn-add-item-table" onClick={onAdd} disabled={disabled}>
                    <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="table-wrapper" ref={tableRef}>
                <table className="items-table fab-items-table">
                    <thead>
                        <tr>
                            <th className="product-col">{translations?.productName || 'Product'} <span className="required">*</span></th>
                            <th className="color-col">{translations?.colorCode || 'Color'}</th>
                            <th className="batch-col">{translations?.batchNumber || 'Batch'}</th>
                            <th className="stock-col">{translations?.availableStock || 'Stock'}</th>
                            <th className="rolls-col">{translations?.rolls || 'Rolls'}</th>
                            <th className="unit-col">{translations?.unit || 'Unit'} <span className="required">*</span></th>
                            <th className="price-col">{translations?.price || 'Price'} <span className="required">*</span></th>
                            <th className="qty-col">{translations?.quantity || 'Qty'} <span className="required">*</span></th>
                            <th className="total-col">{translations?.total || 'Total'}</th>
                            <th className="action-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const availableStock = getAvailableStock(item, item.unit);
                            const isLowStock = availableStock <= (item.stock_alert || 5);
                            const maxRolls = parseInt(item.total_rolls || 0);
                            const currentRolls = parseInt(item.rolls || 0);
                            const isRollsExceed = currentRolls > maxRolls;
                            return (
                                <tr key={index} className={errors[`quantity_${index}`] || isRollsExceed ? 'error-row' : ''}>
                                    <td><input type="text" value={item.product_name} readOnly className="readonly-input" /></td>
                                    <td><input type="text" value={item.product_colorcode || ''} readOnly className="readonly-input" /></td>
                                    <td><input type="text" value={item.batch_no || ''} readOnly className="readonly-input" /></td>
                                    <td>
                                        <div className={`stock-display ${isLowStock ? 'low-stock' : ''}`}>
                                            {availableStock.toFixed(2)} {item.unit === 'yards' ? (translations?.yards || 'Yards') : (translations?.meters || 'Meters')}
                                            {item.total_rolls > 0 && <span className="rolls-info"> ({item.total_rolls} {translations?.roll || 'Rolls'})</span>}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input 
                                                type="number" 
                                                value={item.rolls || 0} 
                                                onChange={(e) => onItemChange(index, 'rolls', e.target.value)} 
                                                step="1" 
                                                min="0" 
                                                max={maxRolls}
                                                placeholder="Rolls"
                                                disabled={disabled}
                                                className={isRollsExceed ? 'error' : ''}
                                            />
                                            {isRollsExceed && <div className="table-error"><FontAwesomeIcon icon={faExclamationTriangle} />Max: {maxRolls} rolls</div>}
                                        </div>
                                    </td>
                                    <td>
                                        <select value={item.unit} onChange={(e) => onItemChange(index, 'unit', e.target.value)} disabled={disabled} className={errors[`unit_${index}`] ? 'error' : ''}>
                                            {units.map((unit) => (<option key={unit.value} value={unit.value}>{unit.label}</option>))}
                                        </select>
                                    </td>
                                    <td><input type="number" value={item.price_per_unit} onChange={(e) => onItemChange(index, 'price_per_unit', e.target.value)} step="0.01" min="0" placeholder="0.00" disabled={disabled} className={errors[`price_${index}`] ? 'error' : ''} /></td>
                                    <td>
                                        <div className="qty-input-wrapper">
                                            <input type="number" value={item.quantity} onChange={(e) => onItemChange(index, 'quantity', e.target.value)} step="0.001" min="0.001" max={availableStock} placeholder="0.000" disabled={disabled} className={errors[`quantity_${index}`] ? 'error' : ''} />
                                            {errors[`quantity_${index}`] && (<div className="table-error"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`quantity_${index}`]}</div>)}
                                        </div>
                                    </td>
                                    <td><input type="text" value={`${currencySymbol} ${calculateTotal(item.price_per_unit, item.quantity)}`} readOnly disabled className="readonly-input total-display" /></td>
                                    <td><button type="button" className="table-remove-btn" onClick={() => onRemove(index)} disabled={disabled || items.length === 1} title={translations?.remove || 'Remove'}><FontAwesomeIcon icon={faTrash} /></button></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Main Component
const Fab_Sale = ({ saleId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const [currentCurrency, setCurrentCurrency] = useState(() => {
        const saved = localStorage.getItem('ourzest_currency');
        return saved ? JSON.parse(saved) : { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', flag: '🇵🇰', shortcut: 'P' };
    });
    const [currencyRate, setCurrencyRate] = useState(1.0000);

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });

    const [shopInfo, setShopInfo] = useState({ id: shopId, name: currentShop?.shop_name || localStorage.getItem('current_shop_name') || '' });
    const [businessInfo, setBusinessInfo] = useState(null);

    const [formData, setFormData] = useState({
        sale_date: new Date().toISOString().split('T')[0],
        bill_number: '', bill_number_int: 0,
        customer_id: null, customer_name: '', customer_phone: '', customer_address: '', customer_gst: '',
        subtotal: '0.00', discount_percent: '0.00', discount_amount: '0.00',
        tax_percent: '0.00', tax_amount: '0.00',
        grand_total: '0.00', paid_amount: '0.00', balance: '0.00',
        payment_method: 'cash', payment_status: 'unpaid',
        o_status: 'shop', notes: '', items: [],
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6',
        currency_code: 'PKR', currency_symbol: 'Rs', currency_rate: 1.0000
    });

    const [todaySummary, setTodaySummary] = useState({
        total_sales: 0, total_amount: 0, total_paid: 0, total_balance: 0
    });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);

    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [searchingCustomer, setSearchingCustomer] = useState(false);
    const [newCustomerInfo, setNewCustomerInfo] = useState(null);
    const [showCustomerModal, setShowCustomerModal] = useState(false);

    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [searchingProducts, setSearchingProducts] = useState(false);

    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [currentSale, setCurrentSale] = useState(null);
    const [showCustomerAccountModal, setShowCustomerAccountModal] = useState(false);

    const toastTimerRef = useRef(null);
    const customerSearchRef = useRef(null);
    const productSearchRef = useRef(null);
    const isSubmittingRef = useRef(false);

    const [toast, setToast] = useState({
        show: false, type: '', message: '', direction: 'ltr', progress: 100, timeLeft: 3
    });

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';
        setToast({ show: true, type, message, direction, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) { clearInterval(timer); setToast(prev => ({ ...prev, show: false })); }
        }, 100);
        toastTimerRef.current = timer;
    }, [currentLanguage]);

    useEffect(() => { return () => { if (toastTimerRef.current) clearInterval(toastTimerRef.current); }; }, []);

    const handleCurrencyChange = (currency) => {
        setCurrentCurrency(currency);
        setFormData(prev => ({ ...prev, currency_code: currency.code, currency_symbol: currency.symbol, currency_rate: currencyRate }));
        showToast('success', `Currency changed to ${currency.code} (${currency.symbol})`);
    };

    const handleRateChange = (rate) => {
        setCurrencyRate(rate);
        setFormData(prev => ({ ...prev, currency_rate: rate }));
    };

    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;
        setIsTranslating(true);
        try {
            const trans = await fabSaleTranslator.getTranslations(lang);
            setTranslations(trans);
            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';
        } catch (error) {
            console.error('Error loading translations:', error);
        } finally { setIsTranslating(false); }
    }, []);

    useEffect(() => {
        loadTranslations(currentLanguage);
        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) { setCurrentLanguage(newLang); loadTranslations(newLang); }
        };
        window.addEventListener('storage', handleLanguageChange);
        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) { setCurrentLanguage(storedLang); loadTranslations(storedLang); }
        }, 500);
        const translationListener = (lang) => { if (lang !== currentLanguage) { setCurrentLanguage(lang); loadTranslations(lang); } };
        fabSaleTranslator.addListener(translationListener);
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            fabSaleTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_business_info', username: user.username })
            });
            const data = await response.json();
            if (data.status === 'success') setBusinessInfo(data.data);
        } catch (error) { console.error('Error loading business info:', error); }
    }, [user?.username]);

    useEffect(() => { if (currentShop) setShopInfo({ id: currentShop.shop_id, name: currentShop.shop_name }); }, [currentShop]);

    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';
                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };
                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };
                Object.entries(palette).forEach(([key, value]) => document.documentElement.style.setProperty(`--${key}`, value));
            } catch (err) { console.error("Error applying theme:", err); }
        };
        applyTheme(formData.themeColor);
        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) { setFormData(prev => ({ ...prev, themeColor: theme })); applyTheme(theme); }
        };
        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);
        return () => { window.removeEventListener('storage', handleThemeChange); clearInterval(interval); };
    }, [formData.themeColor]);

    useEffect(() => { if (shopId && !saleId) getNextBillNumber(); loadBusinessInfo(); }, [shopId, saleId, loadBusinessInfo]);
    useEffect(() => { if (saleId) { setIsEditMode(true); loadSaleData(saleId); } }, [saleId]);
    useEffect(() => { if (shopId && user?.username) loadTodaySummary(); }, [shopId, user?.username]);
    useEffect(() => { calculateTotals(); }, [formData.items, formData.discount_percent, formData.tax_percent]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customerSearchRef.current && !customerSearchRef.current.contains(event.target)) setShowCustomerDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (productSearchRef.current && !productSearchRef.current.contains(event.target)) setShowProductDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getNextBillNumber = async () => {
        if (!shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_next_bill_number', shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setFormData(prev => ({ ...prev, bill_number: data.data.bill_number, bill_number_int: data.data.bill_number_int }));
            }
        } catch (error) { console.error('Error getting next bill number:', error); }
    };

    const loadTodaySummary = async () => {
        if (!user?.username || !shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_today_summary', username: user.username, shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') setTodaySummary(data.data);
        } catch (error) { console.error('Error loading today summary:', error); }
    };

    const loadSaleData = async (id) => {
        if (!user?.username || !shopId) return;
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id: id, username: user.username, shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const sale = data.data;
                setFormData(prev => ({
                    ...prev,
                    sale_date: sale.sale_date || new Date().toISOString().split('T')[0],
                    bill_number: sale.bill_number || '',
                    bill_number_int: sale.bill_number_int || 0,
                    customer_id: sale.customer_id || null,
                    customer_name: sale.customer_name || '',
                    customer_phone: sale.customer_phone || '',
                    customer_address: sale.customer_address || '',
                    customer_gst: sale.customer_gst || '',
                    subtotal: sale.subtotal || '0.00',
                    discount_percent: sale.discount_percent || '0.00',
                    discount_amount: sale.discount_amount || '0.00',
                    tax_percent: sale.tax_percent || '0.00',
                    tax_amount: sale.tax_amount || '0.00',
                    grand_total: sale.grand_total || '0.00',
                    paid_amount: sale.paid_amount || '0.00',
                    balance: sale.balance || '0.00',
                    payment_method: sale.payment_method || 'cash',
                    payment_status: sale.payment_status || 'unpaid',
                    o_status: sale.o_status || 'shop',
                    notes: sale.notes || '',
                    items: sale.items.map(item => ({ 
                        ...item, 
                        rolls: item.rolls || 0,
                        total_yards: item.total_yards || 0,
                        total_meters: item.total_meters || 0,
                        total_rolls: item.total_rolls || 0
                    })) || [],
                    currency_code: sale.currency_code || 'PKR',
                    currency_symbol: sale.currency_symbol || 'Rs',
                    currency_rate: sale.currency_rate || 1.0000
                }));
                if (sale.currency_code) {
                    setCurrentCurrency({
                        code: sale.currency_code,
                        symbol: sale.currency_symbol || 'Rs',
                        name: sale.currency_code,
                        flag: '🇵🇰',
                        shortcut: sale.currency_code.charAt(0)
                    });
                    setCurrencyRate(parseFloat(sale.currency_rate) || 1.0000);
                }
            } else { showToast('error', data.message || 'Failed to load sale data'); }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally { setLoading(false); }
    };

    const searchCustomers = useCallback(debounce(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2 || !shopId) {
            setCustomerSearchResults([]); setShowCustomerDropdown(false); return;
        }
        setSearchingCustomer(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/sell.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_customer', search_term: searchTerm, shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') {
                const customers = data.data || [];
                setCustomerSearchResults(customers);
                setShowCustomerDropdown(customers.length > 0);
            } else { setCustomerSearchResults([]); setShowCustomerDropdown(false); }
        } catch (error) {
            console.error('Error searching customers:', error); setCustomerSearchResults([]); setShowCustomerDropdown(false);
        } finally { setSearchingCustomer(false); }
    }, 250), [shopId]);

    const selectCustomer = (customer) => {
        setFormData(prev => ({
            ...prev,
            customer_id: customer.customerid || customer.customer_id,
            customer_name: customer.customer_name || customer.name,
            customer_phone: customer.phone || '',
            customer_address: customer.address || '',
            customer_gst: customer.gst_no || ''
        }));
        setShowCustomerDropdown(false);
        setCustomerSearchResults([]);
        showToast('success', `${translations?.customerSelected || 'Customer selected'}: ${customer.customer_name || customer.name}`);
    };

    const searchProducts = useCallback(debounce(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2 || !user?.username || !shopId) {
            setFilteredProducts([]); setShowProductDropdown(false); return;
        }
        setSearchingProducts(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_sale.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_products', searchTerm: searchTerm, username: user.username, shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') {
                const products = data.data || [];
                const formattedProducts = products.map(product => ({
                    id: product.id,
                    product_name: product.product_name,
                    product_colorcode: product.product_colorcode,
                    batch_no: product.batch_no,
                    unit: product.unit || 'yards',
                    price: product.price || 0,
                    total_yards: parseFloat(product.total_yards) || 0,
                    total_meters: parseFloat(product.total_meters) || 0,
                    total_rolls: parseInt(product.total_rolls) || 0,
                    stock_alert: product.stock_alert || 5
                }));
                setFilteredProducts(formattedProducts);
                setShowProductDropdown(formattedProducts.length > 0);
            } else { 
                setFilteredProducts([]); 
                setShowProductDropdown(false); 
            }
        } catch (error) {
            console.error('Error searching products:', error); 
            setFilteredProducts([]); 
            setShowProductDropdown(false);
        } finally { 
            setSearchingProducts(false); 
        }
    }, 250), [user?.username, shopId]);

    const handleProductSelect = (product) => {
        const existingItemIndex = formData.items.findIndex(item => item.product_id === product.id);
        
        const availableStockYards = parseFloat(product.total_yards) || 0;
        const availableStockMeters = parseFloat(product.total_meters) || 0;
        const availableRolls = parseInt(product.total_rolls) || 0;
        
        if (existingItemIndex >= 0) {
            const updatedItems = [...formData.items];
            const currentQty = parseFloat(updatedItems[existingItemIndex].quantity) || 0;
            const currentUnit = updatedItems[existingItemIndex].unit || 'yards';
            const currentRolls = parseInt(updatedItems[existingItemIndex].rolls) || 0;
            
            const maxQty = currentUnit === 'yards' ? availableStockYards : availableStockMeters;
            
            if (currentQty + 1 <= maxQty && currentRolls + 1 <= availableRolls) {
                updatedItems[existingItemIndex].quantity = (currentQty + 1).toString();
                updatedItems[existingItemIndex].rolls = (currentRolls + 1).toString();
                setFormData(prev => ({ ...prev, items: updatedItems }));
                showToast('success', `Added another ${product.product_name}`);
            } else {
                showToast('error', `Cannot add more than available stock (Max: ${maxQty} ${currentUnit}, ${availableRolls} rolls)`);
            }
        } else {
            const newItem = {
                product_id: product.id,
                product_name: product.product_name,
                product_colorcode: product.product_colorcode,
                batch_no: product.batch_no,
                unit: product.unit || 'yards',
                price_per_unit: product.price,
                quantity: '1',
                rolls: '1',
                total: product.price,
                total_yards: product.total_yards,
                total_meters: product.total_meters,
                total_rolls: product.total_rolls,
                stock_alert: product.stock_alert
            };
            setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
            showToast('success', `${product.product_name} added to cart`);
        }
        setProductSearchTerm('');
        setShowProductDropdown(false);
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        updatedItems[index] = { ...updatedItems[index], [field]: value };
        
        if (field === 'quantity') {
            const qty = parseFloat(value) || 0;
            const maxQty = updatedItems[index].unit === 'yards' 
                ? parseFloat(updatedItems[index].total_yards || 0)
                : parseFloat(updatedItems[index].total_meters || 0);
            if (qty > maxQty) {
                setErrors(prev => ({ ...prev, [`quantity_${index}`]: `${translations?.quantityExceedsStock || 'Quantity exceeds available stock'} (Max: ${maxQty} ${updatedItems[index].unit})` }));
            } else {
                setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`quantity_${index}`]; return newErrors; });
            }
        }
        
        if (field === 'rolls') {
            const rolls = parseInt(value) || 0;
            const maxRolls = parseInt(updatedItems[index].total_rolls || 0);
            if (rolls > maxRolls) {
                setErrors(prev => ({ ...prev, [`rolls_${index}`]: `${translations?.rollsExceedsStock || 'Rolls exceed available stock'} (Max: ${maxRolls} rolls)` }));
            } else {
                setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`rolls_${index}`]; return newErrors; });
            }
        }
        
        if (field === 'unit') {
            updatedItems[index].quantity = '';
            updatedItems[index].total = 0;
        }
        
        if (field === 'price_per_unit' || field === 'quantity') {
            const price = parseFloat(updatedItems[index].price_per_unit) || 0;
            const qty = parseFloat(updatedItems[index].quantity) || 0;
            updatedItems[index].total = price * qty;
        }
        
        setFormData(prev => ({ ...prev, items: updatedItems }));
        if (errors[`${field}_${index}`]) {
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`${field}_${index}`]; return newErrors; });
        }
    };

    const addItem = () => { if (productSearchRef.current) { const input = productSearchRef.current.querySelector('input'); if (input) input.focus(); } };
    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({ ...prev, items: updatedItems }));
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`quantity_${index}`]; return newErrors; });
        }
    };

    const calculateTotals = () => {
        let subtotal = 0;
        formData.items.forEach(item => { const price = parseFloat(item.price_per_unit) || 0; const quantity = parseFloat(item.quantity) || 0; subtotal += price * quantity; });
        const discountPercent = parseFloat(formData.discount_percent) || 0;
        const discountAmount = (subtotal * discountPercent) / 100;
        const taxPercent = parseFloat(formData.tax_percent) || 0;
        const taxAmount = ((subtotal - discountAmount) * taxPercent) / 100;
        const grandTotal = subtotal - discountAmount + taxAmount;
        const paid = parseFloat(formData.paid_amount) || 0;
        const balance = grandTotal - paid;
        const paymentStatus = balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid');
        setFormData(prev => ({
            ...prev, subtotal: subtotal.toFixed(2), discount_amount: discountAmount.toFixed(2),
            tax_amount: taxAmount.toFixed(2), grand_total: grandTotal.toFixed(2),
            balance: balance.toFixed(2), payment_status: paymentStatus
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required';
        } else {
            formData.items.forEach((item, index) => {
                const qty = parseFloat(item.quantity) || 0;
                const maxQty = item.unit === 'yards' 
                    ? parseFloat(item.total_yards || 0)
                    : parseFloat(item.total_meters || 0);
                if (qty > maxQty) newErrors[`quantity_${index}`] = `${translations?.quantityExceedsStock || 'Quantity exceeds available stock'} (Max: ${maxQty} ${item.unit})`;
                if (qty <= 0) newErrors[`quantity_${index}`] = translations?.invalidQuantity || 'Please enter a valid quantity';
                
                const rolls = parseInt(item.rolls) || 0;
                const maxRolls = parseInt(item.total_rolls || 0);
                if (rolls > maxRolls) newErrors[`rolls_${index}`] = `${translations?.rollsExceedsStock || 'Rolls exceed available stock'} (Max: ${maxRolls} rolls)`;
                if (rolls <= 0) newErrors[`rolls_${index}`] = 'Please enter a valid number of rolls';
                
                const price = parseFloat(item.price_per_unit) || 0;
                if (price <= 0) newErrors[`price_${index}`] = translations?.invalidPrice || 'Please enter a valid price';
                if (!item.unit) newErrors[`unit_${index}`] = 'Please select a unit';
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'customer_name') searchCustomers(value);
        if (name === 'discount_percent' || name === 'tax_percent') calculateTotals();
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handlePaidAmountChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, paid_amount: value }));
        const paid = parseFloat(value) || 0;
        const grandTotal = parseFloat(formData.grand_total) || 0;
        const balance = grandTotal - paid;
        setFormData(prev => ({ ...prev, balance: balance.toFixed(2), payment_status: balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid') }));
    };

    const handlePrintFromModal = () => { setShowReceiptModal(false); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isSubmittingRef.current) {
            console.log('Submission already in progress, ignoring...');
            return;
        }
        
        if (!shopId) { 
            showToast('error', translations?.noShopSelected || 'No shop selected. Please select a shop first.'); 
            return; 
        }
        
        if (!validateForm()) { 
            const firstError = document.querySelector('.has-error, .error-message-modern'); 
            if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' }); 
            return; 
        }
        
        isSubmittingRef.current = true;
        setSaving(true);
        
        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) throw new Error("User not authenticated");
            const action = isEditMode ? 'edit' : 'add';
            const endpoint = 'https://ourzestapp.online/oz_backend/fab_sale.php';
            
            const preparedItems = formData.items.map(item => ({
                product_id: item.product_id,
                product_name: item.product_name,
                product_colorcode: item.product_colorcode || '',
                batch_no: item.batch_no || '',
                unit: item.unit,
                quantity: parseFloat(item.quantity),
                rolls: parseInt(item.rolls) || 0,
                price_per_unit: parseFloat(item.price_per_unit),
                total: parseFloat(item.price_per_unit) * parseFloat(item.quantity)
            }));
            
            const payload = {
                action: action,
                username: currentUsername,
                shop_id: shopId,
                o_status: formData.o_status,
                sale_date: formData.sale_date,
                customer_name: formData.customer_name,
                customer_phone: formData.customer_phone,
                customer_address: formData.customer_address,
                customer_gst: formData.customer_gst,
                subtotal: formData.subtotal,
                discount_percent: formData.discount_percent,
                discount_amount: formData.discount_amount,
                tax_percent: formData.tax_percent,
                tax_amount: formData.tax_amount,
                grand_total: formData.grand_total,
                paid_amount: formData.paid_amount,
                balance: formData.balance,
                payment_method: formData.payment_method,
                payment_status: formData.payment_status,
                notes: formData.notes,
                currency_code: currentCurrency.code,
                currency_symbol: currentCurrency.symbol,
                currency_rate: currencyRate,
                items: preparedItems
            };
            
            if (isEditMode && saleId) payload.id = saleId;
            
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            const data = await response.json();
            
            if (data.status === 'success') {
                const successMessage = isEditMode ? translations?.saleUpdated || 'Fabric sale updated successfully!' : translations?.saleCompleted || 'Fabric sale completed successfully!';
                showToast('success', successMessage);
                
                const saleForReceipt = {
                    ...data.data,
                    items: data.data.items || preparedItems,
                    subtotal: formData.subtotal,
                    discount_amount: formData.discount_amount,
                    tax_amount: formData.tax_amount,
                    grand_total: formData.grand_total,
                    paid_amount: formData.paid_amount,
                    balance: formData.balance,
                    payment_method: formData.payment_method,
                    payment_status: formData.payment_status,
                    o_status: formData.o_status,
                    customer_name: formData.customer_name,
                    customer_phone: formData.customer_phone,
                    customer_address: formData.customer_address,
                    customer_gst: formData.customer_gst,
                    username: currentUsername,
                    currency_symbol: currentCurrency.symbol,
                    currency_code: currentCurrency.code,
                    currency_rate: currencyRate
                };
                setCurrentSale(saleForReceipt);
                
                if (!isEditMode && data.data?.customer?.is_new) {
                    setNewCustomerInfo({
                        name: data.data.customer.name,
                        customer_code: data.data.customer.customer_code,
                        phone: data.data.customer.phone
                    });
                    setShowCustomerModal(true);
                }
                
                if (!isEditMode) {
                    setFormData(prev => ({
                        ...prev,
                        sale_date: new Date().toISOString().split('T')[0],
                        bill_number: data.data?.bill_number || '',
                        bill_number_int: data.data?.bill_number_int || 0,
                        customer_id: null,
                        customer_name: '',
                        customer_phone: '',
                        customer_address: '',
                        customer_gst: '',
                        subtotal: '0.00',
                        discount_percent: '0.00',
                        discount_amount: '0.00',
                        tax_percent: '0.00',
                        tax_amount: '0.00',
                        grand_total: '0.00',
                        paid_amount: '0.00',
                        balance: '0.00',
                        payment_method: 'cash',
                        payment_status: 'unpaid',
                        o_status: 'shop',
                        notes: '',
                        items: []
                    }));
                    getNextBillNumber();
                    loadTodaySummary();
                }
                
                setErrors({});
                if (onSuccess) onSuccess(data.data?.sale_id || saleId);
                setTimeout(() => { setShowReceiptModal(true); }, 500);
            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave) || 'Failed to save sale');
            }
        } catch (error) {
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
            isSubmittingRef.current = false;
        }
    };

    const handleClear = () => {
        setFormData(prev => ({
            ...prev, sale_date: new Date().toISOString().split('T')[0], bill_number: '', bill_number_int: 0,
            customer_id: null, customer_name: '', customer_phone: '', customer_address: '', customer_gst: '',
            subtotal: '0.00', discount_percent: '0.00', discount_amount: '0.00',
            tax_percent: '0.00', tax_amount: '0.00', grand_total: '0.00', paid_amount: '0.00', balance: '0.00',
            payment_method: 'cash', payment_status: 'unpaid', o_status: 'shop', notes: '', items: []
        }));
        setProductSearchTerm('');
        setCustomerSearchResults([]);
        setShowCustomerDropdown(false);
        setErrors({});
        setIsEditMode(false);
        getNextBillNumber();
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return `${currentCurrency.symbol} 0.00`;
        return `${currentCurrency.symbol} ${parseFloat(amount).toFixed(2)}`;
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            <CustomerInfoModal show={showCustomerModal} customer={newCustomerInfo} onClose={() => setShowCustomerModal(false)} translations={translations} isRTL={isRTL} />
            <FabReceiptModal show={showReceiptModal} sale={currentSale} businessInfo={businessInfo} shopInfo={shopInfo} translations={translations} onPrint={handlePrintFromModal} onClose={() => setShowReceiptModal(false)} currencySymbol={currentCurrency.symbol} />
            <CustomerAccountModal show={showCustomerAccountModal} customerId={formData.customer_id} onClose={() => setShowCustomerAccountModal(false)} translations={translations} />

            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon"><FontAwesomeIcon icon={faScissors} /></div>
                    <div className="header-text">
                        <h1>{isEditMode ? (translations?.editFabSale || 'Edit Fabric Sale') : (translations?.newFabSale || 'New Fabric Sale')}</h1>
                        <p>{translations?.sellFabrics || 'Sell Fabric Products'}</p>
                    </div>
                </div>
                <CurrencySelector currentLanguage={currentLanguage} onCurrencyChange={handleCurrencyChange} onRateChange={handleRateChange} />
                {isTranslating && (<div className="translation-indicator"><FontAwesomeIcon icon={faSpinner} spin /><span>Translating...</span></div>)}
            </div>

            <BusinessInfo businessInfo={businessInfo} translations={translations} />

            <form onSubmit={handleSubmit} className="modern-form">
                <div className="form-section">
                    <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faFileInvoice} />{translations?.saleDetails || 'Fabric Sale Details'}</h3></div>
                    <div className="form-grid-modern">
                        <div className="form-group-modern"><div className="input-container"><FontAwesomeIcon icon={faFileInvoice} className="input-icon" /><input type="text" id="bill_number" value={formData.bill_number} readOnly className="readonly-input" dir="ltr" placeholder=" " /><label htmlFor="bill_number" className="input-label">{translations?.billNumber || 'Bill Number'}</label></div></div>
                        <div className={`form-group-modern ${errors.sale_date ? 'has-error' : ''}`}><div className="input-container"><FontAwesomeIcon icon={faCalendarAlt} className="input-icon" /><input type="date" id="sale_date" name="sale_date" value={formData.sale_date} onChange={handleChange} disabled={saving} dir="ltr" placeholder=" " /><label htmlFor="sale_date" className="input-label">{translations?.date || 'Date'}</label></div></div>
                    </div>
                </div>

                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title"><FontAwesomeIcon icon={faUser} />{translations?.customerDetails || 'Customer Details'}</h3>
                        {formData.customer_id && (<button type="button" className="view-account-btn" onClick={() => setShowCustomerAccountModal(true)}><FontAwesomeIcon icon={faUsers} />{translations?.viewAccount || 'View Account'}</button>)}
                    </div>
                    <div className="form-grid-modern">
                        <div className="form-group-modern customer-search-group" ref={customerSearchRef}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <input type="text" id="customer_name" name="customer_name" value={formData.customer_name} onChange={handleChange} onFocus={() => formData.customer_name.length >= 2 && setShowCustomerDropdown(true)} placeholder=" " disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} autoComplete="off" />
                                <label htmlFor="customer_name" className="input-label">{translations?.customerName || 'Customer Name'}</label>
                                {searchingCustomer && (<div className="search-indicator"><FontAwesomeIcon icon={faSpinner} spin /></div>)}
                            </div>
                            {showCustomerDropdown && customerSearchResults.length > 0 && (<div className="customer-search-dropdown">{customerSearchResults.map(customer => (<div key={customer.customerid || customer.customer_id} className="customer-search-item" onClick={() => selectCustomer(customer)}><div className="customer-name">{customer.customer_name || customer.name}</div>{(customer.phone || customer.phonenumber) && (<div className="customer-phone"><FontAwesomeIcon icon={faPhone} size="xs" />{customer.phone || customer.phonenumber}</div>)}{(customer.customer_code) && (<div className="customer-code"><FontAwesomeIcon icon={faIdCard} size="xs" />{customer.customer_code}</div>)}</div>))}</div>)}
                            {showCustomerDropdown && customerSearchResults.length === 0 && !searchingCustomer && formData.customer_name.length >= 2 && (<div className="customer-search-dropdown empty"><FontAwesomeIcon icon={faExclamationTriangle} /><span>{translations?.noCustomersFound || 'No customers found'}</span></div>)}
                        </div>
                        <div className="form-group-modern"><div className="input-container"><FontAwesomeIcon icon={faPhone} className="input-icon" /><input type="tel" id="customer_phone" name="customer_phone" value={formData.customer_phone} onChange={handleChange} placeholder=" " disabled={saving} dir="ltr" /><label htmlFor="customer_phone" className="input-label">{translations?.customerPhone || 'Phone Number'}</label></div></div>
                        <div className="form-group-modern"><div className="input-container"><FontAwesomeIcon icon={faTag} className="input-icon" /><input type="text" id="customer_gst" name="customer_gst" value={formData.customer_gst} onChange={handleChange} placeholder=" " disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} /><label htmlFor="customer_gst" className="input-label">{translations?.customerGST || 'GST Number'}</label></div></div>
                        <div className="form-group-modern full-width"><div className="input-container"><FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" /><input type="text" id="customer_address" name="customer_address" value={formData.customer_address} onChange={handleChange} placeholder=" " disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} /><label htmlFor="customer_address" className="input-label">{translations?.customerAddress || 'Address'}</label></div></div>
                    </div>
                </div>

                <div className="form-section">
                    <div className="form-group-modern product-search-group" ref={productSearchRef}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faSearch} className="input-icon search-icon" />
                            <input type="text" id="product_search" value={productSearchTerm} onChange={(e) => { setProductSearchTerm(e.target.value); searchProducts(e.target.value); }} onFocus={() => productSearchTerm.length >= 2 && setShowProductDropdown(true)} placeholder=" " disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} className="product-search-input" autoComplete="off" />
                            <label htmlFor="product_search" className="input-label">{translations?.searchProducts || 'Search Fabric Products'}</label>
                            {searchingProducts && (<div className="search-indicator"><FontAwesomeIcon icon={faSpinner} spin /></div>)}
                            <button type="button" className="scan-barcode-btn generate-btn" title={translations?.scanBarcode || 'Scan Barcode'}><FontAwesomeIcon icon={faQrcode} /></button>
                        </div>
                        {showProductDropdown && filteredProducts.length > 0 && (<div className="product-search-dropdown fab-product-dropdown">{filteredProducts.map(product => (<div key={product.id} className="product-search-item" onClick={() => handleProductSelect(product)}><div className="product-item-name"><FontAwesomeIcon icon={faBox} className="product-icon" />{product.product_name}{product.product_colorcode && (<span className="product-color-badge"><FontAwesomeIcon icon={faPalette} />{product.product_colorcode}</span>)}</div><div className="product-item-details">{product.batch_no && (<span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>)}</div><div className="product-item-price-stock"><span className="product-price"><FontAwesomeIcon icon={currentCurrency.code === 'PKR' ? faRupeeSign : faDollarSign} />{currentCurrency.symbol} {parseFloat(product.price).toFixed(2)}</span><span className={`product-stock ${parseFloat(product.total_yards) <= (product.stock_alert || 5) ? 'low-stock' : ''}`}>{translations?.availableStock || 'Stock'}: {parseFloat(product.total_yards).toFixed(2)} Yards / {product.total_rolls} Rolls</span></div></div>))}</div>)}
                        {showProductDropdown && productSearchTerm.length >= 2 && filteredProducts.length === 0 && !searchingProducts && (<div className="product-search-dropdown empty"><FontAwesomeIcon icon={faExclamationTriangle} /><span>{translations?.noProductsFound || 'No products found'}</span></div>)}
                    </div>
                </div>

                {formData.items.length > 0 && (<div className="form-section"><FabItemsTable items={formData.items} onItemChange={handleItemChange} onRemove={removeItem} onAdd={addItem} errors={errors} disabled={saving} translations={translations} isRTL={isRTL} currencySymbol={currentCurrency.symbol} /></div>)}

                {formData.items.length > 0 && (
                    <div className="form-section">
                        <div className="section-header"><h3 className="section-title"><FontAwesomeIcon icon={faCalculator} />{translations?.calculations || 'Calculations'}</h3></div>
                        <div className="calculations-grid fab-calculations">
                            <div className="calculation-row"><span className="calculation-label">{translations?.subtotal || 'Subtotal'}:</span><span className="calculation-value">{formatCurrency(formData.subtotal)}</span></div>
                            <div className="calculation-row"><span className="calculation-label">{translations?.discountPercent || 'Discount %'}:</span><div className="discount-input-wrapper"><input type="number" id="discount_percent" name="discount_percent" value={formData.discount_percent} onChange={handleChange} step="0.01" min="0" max="100" placeholder="0.00" disabled={saving} dir="ltr" style={{ textAlign: 'right' }} /></div></div>
                            <div className="calculation-row"><span className="calculation-label">{translations?.discountAmount || 'Discount Amount'}:</span><span className="calculation-value negative">-{formatCurrency(formData.discount_amount)}</span></div>
                            <div className="calculation-row"><span className="calculation-label">{translations?.taxPercent || 'Tax %'}:</span><div className="tax-input-wrapper"><input type="number" id="tax_percent" name="tax_percent" value={formData.tax_percent} onChange={handleChange} step="0.01" min="0" max="100" placeholder="0.00" disabled={saving} dir="ltr" style={{ textAlign: 'right' }} /></div></div>
                            <div className="calculation-row"><span className="calculation-label">{translations?.taxAmount || 'Tax Amount'}:</span><span className="calculation-value positive">+{formatCurrency(formData.tax_amount)}</span></div>
                            <div className="calculation-row grand-total"><span className="calculation-label">{translations?.grandTotal || 'Grand Total'}:</span><span className="calculation-value highlight">{formatCurrency(formData.grand_total)}</span></div>
                            <div className="calculation-row"><span className="calculation-label">{translations?.paymentMethod || 'Payment Method'}:</span><div className="payment-method-selector">
<select value={formData.payment_method} onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))} disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} style={{ width: '100%' }}>
    <option value="cash">{translations?.cash || 'Cash'}</option>
    <option value="card">{translations?.card || 'Card'}</option>
    <option value="upi">{translations?.upi || 'UPI'}</option>
    <option value="bank_transfer">{translations?.bankTransfer || 'Bank Transfer'}</option>
    <option value="credit">{translations?.credit || 'Credit'}</option>
    <option value="borrowed">{translations?.borrowed || 'Borrowed'}</option> {/* NEW */}
    <option value="other">{translations?.other || 'Other'}</option>
</select></div></div>
                            <div className={`calculation-row paid-field ${errors.paid ? 'has-error' : ''}`}><span className="calculation-label">{translations?.paidAmount || 'Paid Amount'}:</span><div className="paid-input-wrapper"><input type="number" id="paid_amount" name="paid_amount" value={formData.paid_amount} onChange={handlePaidAmountChange} step="0.01" min="0" max={formData.grand_total} placeholder="0.00" disabled={saving} dir="ltr" style={{ textAlign: 'right' }} /></div></div>
                            <div className="calculation-row balance"><span className="calculation-label">{translations?.balance || 'Balance'}:</span><span className={`calculation-value ${parseFloat(formData.balance) > 0 ? 'negative' : 'positive'}`}>{formatCurrency(formData.balance)}</span></div>
                            <div className="calculation-row"><span className="calculation-label">{translations?.paymentStatus || 'Payment Status'}:</span><span className={`status-badge ${formData.payment_status}`}>{translations?.[formData.payment_status] || formData.payment_status}</span></div>
                        </div>
                    </div>
                )}

                {formData.items.length > 0 && (
                    <div className="form-section"><div className="form-group-modern"><div className="input-container textarea-container"><FontAwesomeIcon icon={faReceipt} className="input-icon" /><textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} placeholder=" " disabled={saving} rows="3" dir={isRTL ? 'rtl' : 'ltr'}></textarea><label htmlFor="notes" className="input-label">{translations?.notes || 'Notes'}</label></div></div></div>
                )}

                <div className="form-actions-modern">
                    <button type="button" className="btn-modern btn-secondary-modern" onClick={handleClear} disabled={saving}><FontAwesomeIcon icon={faTimes} /><span>{translations?.clear || 'Clear'}</span></button>
                    <button type="submit" className="btn-modern btn-primary-modern" disabled={saving || isTranslating || formData.items.length === 0}>
                        {saving ? (<><div className="btn-spinner"></div><span>{translations?.saving || 'Saving...'}</span></>) : (<><span>{isEditMode ? (translations?.updateSale || 'Update Sale') : (translations?.completeSale || 'Complete Sale')}</span><FontAwesomeIcon icon={faSave} /></>)}
                    </button>
                </div>
            </form>

            {loading && (<div className="loading-overlay"><div className="loading-content"><div className="loading-spinner"></div><p>{translations?.loading || 'Loading...'}</p><p className="loading-subtext">Please wait while we load fabric sale data</p></div></div>)}
        </div>
    );
};

export default Fab_Sale;